import {
  Component,
  HostBinding,
  Input,
  NgZone,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { NavItem } from '../../../vertical/sidebar/nav-item/nav-item';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { isNavRouteActive } from '@core/shell/nav-route-active.utils';
import {
  ConfirmOpenTabDialogComponent,
  ConfirmOpenTabDialogData,
} from '@shared/ui/confirm-open-tab-dialog/confirm-open-tab-dialog.component';
import {
  isPosShellEntryRoute,
  POS_VENTA_ENTRY_ROUTE,
} from '@shared/ui/confirm-open-tab-dialog/confirm-enter-pos';
import { NavIconPairComponent } from '@shared/ui/nav-icon-pair/nav-icon-pair.component';

@Component({
  selector: 'app-horizontal-nav-item',
  imports: [CommonModule, MatIconModule, MatDialogModule, TranslateModule, NavIconPairComponent],
  templateUrl: './nav-item.component.html',
})
export class AppHorizontalNavItemComponent {
  private readonly ngZone = inject(NgZone);
  private readonly dialog = inject(MatDialog);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly router = inject(Router);

  @Input() depth = 0;
  @Input() item: NavItem = {};
  /** Id del módulo cuyo dropdown está abierto (controlado por el sidebar). */
  @Input() openModuleId: number | string | null = null;
  @Output() openModuleIdChange = new EventEmitter<number | string | null>();

  /** Resalta el módulo padre cuando alguna hija está activa. */
  @HostBinding('class.pactive')
  get parentActive(): boolean {
    return !!this.item.children?.length && this.isItemActive(this.item);
  }

  @HostBinding('class.is-open')
  get menuOpen(): boolean {
    return !!this.item.children?.length && this.openModuleId === this.moduleKey();
  }

  moduleKey(): number | string {
    return this.item.id ?? this.item.route ?? this.item.displayName ?? '';
  }

  trackChild(child: NavItem, index: number): string {
    return String(child.route ?? child.displayName ?? index);
  }

  isItemActive(item: NavItem): boolean {
    if (isNavRouteActive(this.router, item.route)) {
      return true;
    }
    return (item.children ?? []).some((child) => this.isItemActive(child));
  }

  onItemSelected(item: NavItem, event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    if (item.children?.length) {
      // Toggle dropdown por clic (estable); no depender de hover.
      const key = this.moduleKey();
      const next = this.openModuleId === key ? null : key;
      this.openModuleIdChange.emit(next);
      return;
    }

    if (!item.route) {
      return;
    }
    this.openModuleIdChange.emit(null);
    this.navigateTo(item.route, item);
  }

  private navigateTo(route: string, item: NavItem): void {
    const url = route.startsWith('/') ? route : `/${route}`;
    if (item.openInNewTab) {
      const data: ConfirmOpenTabDialogData = {
        titleKey: 'sidebar.openCatalogConfirmTitle',
        messageKey: 'sidebar.openCatalogConfirmMessage',
        confirmKey: 'sidebar.openCatalogConfirmAccept',
        cancelKey: 'sidebar.openCatalogConfirmCancel',
      };
      this.dialog
        .open(ConfirmOpenTabDialogComponent, {
          width: '420px',
          maxWidth: '90vw',
          data,
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (!confirmed) {
            return;
          }
          const absolute =
            url.startsWith('http://') || url.startsWith('https://')
              ? url
              : `${window.location.origin}${url}`;
          window.open(absolute, '_blank', 'noopener,noreferrer');
        });
      return;
    }

    this.ngZone.run(() => {
      if (isPosShellEntryRoute(url)) {
        this.adminShellMode.enterPos(url === '/pos' ? POS_VENTA_ENTRY_ROUTE : url);
        return;
      }
      void this.router.navigateByUrl(url);
    });
  }
}
